import client from './client';
import type { ApiResponse, Campaign, CampaignChannel, CampaignResult, CampaignEvent } from '../types';

// Recurrence intervals supported by the backend for evergreen campaigns.
export type RecurrenceInterval = 'daily' | 'weekly' | 'monthly';

// Recurrence fields returned on campaign rows by the Go backend. Defined
// locally rather than in types/index.ts so this feature stays self-contained.
export interface CampaignRecurrenceFields {
  recurring?: boolean;
  recurrence_interval?: RecurrenceInterval | '';
  recurrence_end_date?: string | null;
  parent_campaign_id?: number;
  next_run_at?: string | null;
}

export type CampaignWithRecurrence = Campaign & CampaignRecurrenceFields;

// CampaignStats matches models.CampaignStats from GET /campaigns/{id}/summary
// (and the campaigns list summary endpoint). Note email_reported, not reported.
export interface CampaignStats {
  total: number;
  sent: number;
  opened: number;
  clicked: number;
  submitted_data: number;
  email_reported: number;
  error: number;
  reported?: number;
  errors?: number;
}

export interface CampaignSummaryRow {
  id: number;
  name: string;
  created_date: string;
  launch_date: string;
  send_by_date: string | null;
  completed_date: string | null;
  status: Campaign['status'];
  stats: CampaignStats;
}

// CampaignResultsPayload matches models.CampaignResults from
// GET /campaigns/{id}/results — a wrapper object, not a bare array.
export interface CampaignResultsPayload {
  id: number;
  name: string;
  status: string;
  results?: CampaignResult[];
  timeline?: CampaignEvent[];
}

// CampaignOccurrence is one spawned occurrence of a recurring series, as
// returned by GET /api/campaigns/{id}/occurrences.
export interface CampaignOccurrence {
  id: number;
  name: string;
  created_date: string;
  launch_date: string;
  send_by_date: string | null;
  completed_date: string | null;
  status: string;
  stats?: CampaignStats;
}

// Named resource refs match the legacy Go PostCampaign contract:
// IDs are json:"-" on the server; create resolves by name.
export interface NamedRef {
  name: string;
}

export interface CreateCampaignRequest {
  name: string;
  template?: NamedRef;
  page?: NamedRef;
  smtp: NamedRef;
  groups: NamedRef[];
  url: string;
  launch_date?: string;
  send_by_date?: string;
  recurring?: boolean;
  recurrence_interval?: RecurrenceInterval;
  recurrence_end_date?: string;
  // Multi-channel simulation support.
  channel?: CampaignChannel;
  channel_config?: string;
  // Template variants for per-recipient random assignment (IDs are accepted).
  template_ids?: number[];
  no_whitelist?: boolean;
}

export interface QRCodeEntry {
  rid: string;
  email: string;
  first_name: string;
  last_name: string;
  url: string;
  data_uri: string;
}

export const campaignsApi = {
  // Go returns a bare Campaign[] (not { data: [...] }).
  getAll: (params?: { page?: number; per_page?: number }) =>
    client.get<CampaignWithRecurrence[]>('/campaigns/', { params }),

  getById: (id: number) =>
    client.get<CampaignWithRecurrence>(`/campaigns/${id}`),

  getSummary: () =>
    client.get<{ total: number; campaigns: CampaignSummaryRow[] }>('/campaigns/summary'),

  trackingURL: () => client.get<{ url: string }>('/campaigns/tracking-url'),

  getCampaignSummary: (id: number) =>
    client.get<CampaignSummaryRow>(`/campaigns/${id}/summary`),

  getResults: (id: number) =>
    client.get<CampaignResultsPayload>(`/campaigns/${id}/results`),

  exportResultsCSV: (id: number) =>
    client.get<Blob>(`/campaigns/${id}/results.csv`, { responseType: 'blob' }),

  getQRCodes: (id: number) =>
    client.get<QRCodeEntry[]>(`/campaigns/${id}/qr-codes`),

  getOccurrences: (id: number) =>
    client.get<CampaignOccurrence[]>(`/campaigns/${id}/occurrences`),

  create: (data: CreateCampaignRequest) =>
    client.post<CampaignWithRecurrence>('/campaigns/', data),

  complete: (id: number) =>
    client.post<ApiResponse<Campaign>>(`/campaigns/${id}/complete`),

  stop: (id: number) => client.post<ApiResponse<null>>(`/campaigns/${id}/stop`),

  delete: (id: number) =>
    client.delete<ApiResponse<null>>(`/campaigns/${id}`),
};
